import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { updateProfile, getAdditionalUserInfo } from 'firebase/auth';
import { saveUserOnboarding } from '../db';
import { toast } from '../utils/feedback';
import { X, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthPage = () => {
  const { login, signup, logout, sendVerification, resetPassword, googleSignIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialIsLogin = location.state?.isLogin ?? true;
  
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.classList.add('auth-bg');
    document.body.classList.add('auth-bg');
    const rootEl = document.getElementById('root');
    if (rootEl) rootEl.classList.add('full-width');
    
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const originalThemeColor = themeMeta ? themeMeta.getAttribute('content') : '#121016';
    if (themeMeta) themeMeta.setAttribute('content', '#181126');
    
    return () => {
      document.documentElement.classList.remove('auth-bg');
      document.body.classList.remove('auth-bg');
      if (rootEl) rootEl.classList.remove('full-width');
      if (themeMeta) themeMeta.setAttribute('content', originalThemeColor);
    };
  }, []);

  const [error, setError] = useState('');

  useEffect(() => {
    const savedError = sessionStorage.getItem('authError');
    if (savedError) {
      setError(savedError);
      sessionStorage.removeItem('authError');
    }
  }, []);

  const [isLogin, setIsLogin] = useState(initialIsLogin);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  
  // Auth fields
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Profile fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const [loading, setLoading] = useState(false);

  // Calculate password strength
  const calculateStrength = (pwd) => {
    let score = 0;
    if (pwd.length > 0) score += 1;
    if (pwd.length >= 8) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    return score; // 0 to 5
  };

  const strengthScore = calculateStrength(password);
  
  const getStrengthColor = () => {
    if (strengthScore <= 1) return 'var(--danger-color)';
    if (strengthScore <= 3) return 'orange';
    if (strengthScore >= 4) return 'var(--secondary-color)';
    return 'var(--border-color)';
  };

  const getStrengthLabel = () => {
    if (password.length === 0) return '';
    if (strengthScore <= 2) return 'Faible';
    if (strengthScore === 3) return 'Moyen';
    return 'Fort';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!identifier || !password || (!isLogin && (!firstName || !lastName))) {
      return setError('Remplis tous les champs.');
    }

    if (!isLogin && password !== confirmPassword) {
      return setError('Les mots de passe ne correspondent pas.');
    }

    if (!isLogin && strengthScore < 3) {
      return setError('Le mot de passe est trop faible.');
    }

    setLoading(true);

    try {
      if (isLogin) {
        await login(identifier, password);
      } else {
        // Sign up
        const userCredential = await signup(identifier, password);
        const user = userCredential.user;
        
        // Update profile with name
        await updateProfile(user, { displayName: `${firstName} ${lastName}` });
        
        await sendVerification(user);
      }
    } catch (err) {
      console.error('Auth error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError("Ce compte existe déjà. Nous t'avons basculé sur la connexion.");
        setIsLogin(true);
      } else if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        setError('Identifiants incorrects.');
      } else if (err.code === 'auth/weak-password') {
        setError('Le mot de passe doit faire au moins 6 caractères.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Adresse email invalide.');
      } else {
        setError('Une erreur est survenue.');
      }
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      await googleSignIn();
    } catch (err) {
      console.error('Erreur Google Auth:', err);
      if (err.code === 'auth/email-already-in-use' || err.code === 'auth/credential-already-in-use') {
        setError("Ce compte existe déjà. Connecte-toi.");
        setIsLogin(true);
      } else {
        setError(err.message || "Erreur lors de la connexion avec Google.");
      }
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword(identifier);
      setResetSent(true);
    } catch (err) {
      console.error('Reset error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-email') {
        setError("Cette adresse e-mail n'est pas reconnue.");
      } else {
        setError("Une erreur est survenue lors de l'envoi de l'e-mail.");
      }
    }
    setLoading(false);
  };

  return (
    <div className="auth-page-wrapper" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.2rem',
      animation: 'fadeIn 0.5s ease-out',
      overflowY: 'auto',
      boxSizing: 'border-box'
    }}>
      <style>{`
        .auth-card {
          width: 100%;
          max-width: 440px;
          margin: auto;
          background: var(--card, #1c1a27);
          border: 1px solid var(--border, rgba(255,255,255,0.08));
          border-radius: 20px;
          padding: 1.35rem 1.7rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          position: relative;
          box-sizing: border-box;
        }
        @media (min-width: 769px) {
          .auth-card {
            max-width: 472px !important;
            padding: 1.55rem 2rem !important;
            border-radius: 22px !important;
          }
          .auth-card .auth-logo {
            height: 58px !important;
          }
          .auth-card .auth-title {
            font-size: 1.55rem !important;
          }
          .auth-card .auth-subtitle {
            font-size: 0.86rem !important;
          }
          .auth-card .auth-tab-btn {
            padding: 0.46rem 0.7rem !important;
            font-size: 0.9rem !important;
          }
          .auth-card .form-control {
            padding: 0.65rem 0.85rem !important;
            font-size: 0.94rem !important;
          }
          .auth-card .auth-submit-btn {
            padding: 0.68rem !important;
            font-size: 1rem !important;
            margin-top: 0.7rem !important;
          }
          .auth-card .auth-google-btn {
            padding: 0.66rem !important;
            font-size: 0.94rem !important;
          }
        }
        @media (max-width: 600px) {
          .auth-card {
            padding: 1.15rem 1rem !important;
            border-radius: 18px !important;
            max-width: 100% !important;
          }
          .auth-page-wrapper {
            padding: 0.6rem !important;
          }
          .auth-card .auth-logo {
            height: 44px !important;
          }
          .auth-card .auth-title {
            font-size: 1.3rem !important;
            margin-bottom: 0.15rem !important;
          }
          .auth-card .auth-subtitle {
            font-size: 0.78rem !important;
          }
          .auth-card .auth-header-wrap {
            margin-bottom: 0.65rem !important;
          }
          .auth-card .auth-tabs-wrap {
            margin-bottom: 0.65rem !important;
            padding: 2px !important;
          }
          .auth-card .auth-tab-btn {
            padding: 0.35rem 0.5rem !important;
            font-size: 0.84rem !important;
          }
          .auth-card form {
            gap: 0.45rem !important;
          }
          .auth-card .form-label {
            font-size: 0.7rem !important;
            margin-bottom: 0.15rem !important;
          }
          .auth-card .form-control {
            padding: 0.52rem 0.7rem !important;
            font-size: 0.88rem !important;
            border-radius: 9px !important;
          }
          .auth-card .auth-submit-btn {
            padding: 0.58rem !important;
            font-size: 0.94rem !important;
            margin-top: 0.5rem !important;
            border-radius: 9px !important;
          }
          .auth-card .auth-divider-wrap {
            margin: 0.35rem 0 !important;
          }
          .auth-card .auth-google-btn {
            padding: 0.54rem !important;
            font-size: 0.88rem !important;
            border-radius: 9px !important;
          }
          .auth-card .auth-switch-wrap {
            margin-top: 0.5rem !important;
            font-size: 0.8rem !important;
          }
          .auth-card .auth-footer-wrap {
            margin-top: 0.35rem !important;
            font-size: 0.7rem !important;
          }
        }
      `}</style>
      <div style={{ position: 'fixed', top: '10%', left: '20%', width: 'min(300px, 60vw)', height: 'min(300px, 60vw)', background: 'var(--primary-color)', borderRadius: '50%', filter: 'blur(100px)', opacity: 'var(--blob-opacity)', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', bottom: '10%', right: '20%', width: 'min(250px, 50vw)', height: 'min(250px, 50vw)', background: 'var(--secondary-color)', borderRadius: '50%', filter: 'blur(100px)', opacity: 'var(--blob-opacity)', pointerEvents: 'none' }} />

      <div className="auth-card">
        <button 
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '12px', left: '12px', background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '0.35rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s', zIndex: 10 }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          title="Retour"
        >
          <ArrowLeft size={20} />
        </button>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, var(--primary-color), transparent)', opacity: 0.5 }} />

        <div className="auth-header-wrap" style={{ textAlign: 'center', marginBottom: '0.85rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.35rem' }}>
            <img className="auth-logo" loading="lazy" src="/logos/logo-officiel-transparent.png" alt="Logo Masse-terclass" style={{ height: '52px', filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.4))' }} />
          </div>
          <h2 className="auth-title" style={{ 
            fontSize: '1.45rem', 
            background: 'linear-gradient(to right, var(--text-primary), var(--text-secondary))', 
            WebkitBackgroundClip: 'text', 
            WebkitTextFillColor: 'transparent',
            margin: 0, marginBottom: '0.2rem'
          }}>
            {isForgotPassword ? 'Mot de passe oublié' : (isLogin ? 'Bonjour !' : 'Créer un compte')}
          </h2>
          <p className="auth-subtitle" style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0 }}>
            {isForgotPassword ? 'Reçois un lien pour réinitialiser ton mot de passe' : (isLogin ? 'Entre tes identifiants pour continuer' : 'Rejoins la Masse-terclass')}
          </p>
        </div>

        {!isForgotPassword && (
          <div className="auth-tabs-wrap" style={{ display: 'flex', background: 'rgba(255,255,255,0.06)', borderRadius: '12px', padding: '3px', marginBottom: '0.85rem', border: '1px solid var(--border-color)' }}>
            <button 
              type="button" 
              className="auth-tab-btn"
              onClick={() => { setIsLogin(true); setError(''); }}
              style={{ 
                flex: 1, padding: '0.42rem 0.6rem', border: 'none', borderRadius: '9px',
                backgroundColor: isLogin ? 'var(--primary-color)' : 'transparent',
                color: isLogin ? '#fff' : 'var(--text-secondary)',
                fontWeight: isLogin ? '700' : '500', fontSize: '0.88rem', cursor: 'pointer', transition: 'all 0.2s'
              }}
            >
              Se connecter
            </button>
            <button 
              type="button" 
              className="auth-tab-btn"
              onClick={() => { setIsLogin(false); setError(''); }}
              style={{ 
                flex: 1, padding: '0.42rem 0.6rem', border: 'none', borderRadius: '9px',
                backgroundColor: !isLogin ? 'var(--primary-color)' : 'transparent',
                color: !isLogin ? '#fff' : 'var(--text-secondary)',
                fontWeight: !isLogin ? '700' : '500', fontSize: '0.88rem', cursor: 'pointer', transition: 'all 0.2s'
              }}
            >
              Créer un compte
            </button>
          </div>
        )}
        
        {error && (
          <div style={{ 
            backgroundColor: 'rgba(255, 107, 107, 0.1)', 
            border: '1px solid rgba(255, 107, 107, 0.2)',
            color: '#ff6b6b', padding: '0.65rem', borderRadius: '10px', 
            marginBottom: '0.85rem', textAlign: 'center', fontSize: '0.85rem',
            animation: 'shake 0.4s ease-in-out'
          }}>
            {error}
          </div>
        )}

        {isForgotPassword ? (
          resetSent ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', border: '1px solid rgba(76, 175, 80, 0.2)', color: '#4caf50', padding: '0.75rem', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.88rem' }}>
                Un e-mail de réinitialisation a été envoyé à <strong>{identifier}</strong>. Vérifie ta boîte de réception (et tes spams).
              </div>
              <button 
                type="button" 
                className="btn btn-outline" 
                onClick={() => { setIsForgotPassword(false); setResetSent(false); }}
                style={{ width: '100%', padding: '0.65rem', borderRadius: '10px', fontSize: '0.95rem', marginBottom: '1.25rem' }}
              >
                Retour à la connexion
              </button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.2rem' }}>Email</label>
                <input 
                  type="email"
                  className="form-control"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="ton@email.com"
                  required
                  style={{ 
                    backgroundColor: 'var(--glass-input-bg)', border: '1px solid var(--glass-border)', 
                    padding: '0.6rem 0.8rem', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.92rem',
                    transition: 'all 0.3s ease', width: '100%'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--glass-border)'}
                />
              </div>
              <button 
                type="submit" 
                style={{ 
                  width: '100%', marginTop: '0.4rem', marginBottom: '1.1rem', padding: '0.65rem', color: '#fff',
                  borderRadius: '10px', fontSize: '0.95rem', fontWeight: 'bold',
                  background: 'linear-gradient(135deg, var(--primary-color), #8c7fbf)',
                  border: 'none', boxShadow: '0 4px 15px rgba(168, 159, 207, 0.3)',
                  cursor: 'pointer', transition: 'opacity 0.2s',
                  opacity: loading ? 0.7 : 1
                }}
                disabled={loading}
              >
                {loading ? 'Envoi...' : "Envoyer le lien"}
              </button>
            </form>
          )
        ) : (
          <>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
              {!isLogin && (
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                    <label className="form-label" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.2rem' }}>Prénom</label>
                    <input 
                      type="text"
                      className="form-control"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Alex"
                      required
                      style={{ 
                        backgroundColor: 'var(--glass-input-bg)', border: '1px solid var(--glass-border)', 
                        padding: '0.6rem 0.8rem', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.92rem',
                        transition: 'all 0.3s ease', width: '100%'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--glass-border)'}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                    <label className="form-label" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.2rem' }}>Nom</label>
                    <input 
                      type="text"
                      className="form-control"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Dupont"
                      required
                      style={{ 
                        backgroundColor: 'var(--glass-input-bg)', border: '1px solid var(--glass-border)', 
                        padding: '0.6rem 0.8rem', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.92rem',
                        transition: 'all 0.3s ease', width: '100%'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--glass-border)'}
                    />
                  </div>
                </div>
              )}

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.2rem' }}>Email</label>
                <input 
                  type="email"
                  className="form-control"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="ton@email.com"
                  required
                  style={{ 
                    backgroundColor: 'var(--glass-input-bg)', border: '1px solid var(--glass-border)', 
                    padding: '0.6rem 0.8rem', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.92rem',
                    transition: 'all 0.3s ease', width: '100%'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--glass-border)'}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isLogin ? '1fr' : '1fr 1fr', gap: '0.6rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.2rem' }}>Mot de passe</label>
                  <input 
                    type="password"
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={isLogin ? undefined : 6}
                    style={{ 
                      backgroundColor: 'var(--glass-input-bg)', border: '1px solid var(--glass-border)', 
                      padding: '0.6rem 0.8rem', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.92rem',
                      transition: 'all 0.3s ease', width: '100%'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--glass-border)'}
                  />
                  
                  {isLogin && (
                    <div style={{ textAlign: 'right', marginTop: '0.25rem' }}>
                      <span 
                        onClick={() => { setIsForgotPassword(true); setError(''); }}
                        style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', cursor: 'pointer', transition: 'color 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary-color)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                      >
                        Mot de passe oublié ?
                      </span>
                    </div>
                  )}
                  
                  {!isLogin && password.length > 0 && (
                    <div style={{ marginTop: '0.35rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', marginBottom: '3px', color: 'var(--text-secondary)' }}>
                        <span>Force</span>
                        <span style={{ color: getStrengthColor(), fontWeight: 'bold' }}>{getStrengthLabel()}</span>
                      </div>
                      <div style={{ height: '3px', background: 'var(--glass-border)', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ 
                          height: '100%', 
                          width: `${(strengthScore / 5) * 100}%`, 
                          background: getStrengthColor(),
                          transition: 'all 0.3s ease'
                        }} />
                      </div>
                    </div>
                  )}
                </div>

                {!isLogin && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.2rem' }}>Confirmer</label>
                    <input 
                      type="password"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      style={{ 
                        backgroundColor: 'var(--glass-input-bg)', border: '1px solid var(--glass-border)', 
                        padding: '0.6rem 0.8rem', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.92rem',
                        transition: 'all 0.3s ease', width: '100%'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--glass-border)'}
                    />
                    {password !== confirmPassword && confirmPassword.length > 0 && (
                      <div style={{ color: '#ff6b6b', fontSize: '0.72rem', marginTop: '0.25rem' }}>
                        Les mots de passe ne correspondent pas
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button 
                type="submit" 
                className="btn btn-primary auth-submit-btn" 
                style={{ 
                  width: '100%', marginTop: '0.65rem', padding: '0.65rem', 
                  borderRadius: '10px', fontSize: '0.98rem', fontWeight: 'bold',
                  background: 'linear-gradient(135deg, var(--primary-color), #8c7fbf)',
                  border: 'none', boxShadow: '0 4px 15px rgba(168, 159, 207, 0.3)',
                  position: 'relative', overflow: 'hidden'
                }}
                disabled={loading}
              >
                {loading ? 'Connexion...' : (isLogin ? 'Se connecter' : "S'inscrire")}
              </button>
            </form>

            <div className="auth-divider-wrap" style={{ marginTop: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', margin: '0.45rem 0' }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }}></div>
                <span style={{ margin: '0 0.6rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>ou</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }}></div>
              </div>
              
              <button 
                type="button" 
                className="auth-google-btn"
                onClick={handleGoogleSignIn}
                disabled={loading}
                style={{ 
                  width: '100%', padding: '0.62rem', 
                  backgroundColor: '#fff', color: '#333',
                  borderRadius: '10px', fontSize: '0.92rem', fontWeight: 'bold',
                  border: '1px solid #ddd',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
                  cursor: 'pointer', transition: 'background-color 0.2s',
                  opacity: loading ? 0.7 : 1
                }}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                  <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                    <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                    <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                    <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                    <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
                  </g>
                </svg>
                Continuer avec Google
              </button>
            </div>
          </>
        )}

        {!isForgotPassword && (
          <div className="auth-switch-wrap" style={{ marginTop: '0.65rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
            {isLogin ? "Pas encore de compte ? " : "Déjà un compte ? "}
            <span 
              onClick={() => { setIsLogin(!isLogin); setError(''); setPassword(''); setConfirmPassword(''); }}
              style={{ color: 'var(--primary-color)', cursor: 'pointer', fontWeight: 'bold', borderBottom: '1px solid var(--primary-color)', paddingBottom: '2px' }}
            >
              {isLogin ? "S'inscrire" : "Se connecter"}
            </span>
          </div>
        )}

        <div className="auth-footer-wrap" style={{ marginTop: '0.45rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.72rem', opacity: 0.7 }}>
          Un problème ? <a href="mailto:contact@masse-terclass.com" style={{ color: 'var(--text-secondary)', textDecoration: 'underline' }}>contact@masse-terclass.com</a>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
